import { useEffect, useState } from 'react';
import {
  Modal,
  Button,
  TextInput,
  Stack,
  Group,
  Avatar,
  Text,
  Title,
  Flex,
  ActionIcon,
  Anchor,
  Divider,
} from '@mantine/core';
import { Controller, useForm } from 'react-hook-form';
import { HiArrowLeft } from 'react-icons/hi';
import { isMobile } from 'react-device-detect';
import AsyncSelect from 'react-select/async';
import {
  type SingleValue,
  type MenuProps,
  type InputProps,
  type InputActionMeta,
  components as selectComponents,
} from 'react-select';
import useNotification from '@hooks/useNotification';
import defaultAvatar from '@assets/default-avatar.png';
import { MIN_SEARCH_CHARS } from '@constants/various';
import { isValidEmail } from '@utils/FormUtils';
import { capitalizeName } from '@utils/StringUtils';
import { Step2Content } from './Step2Content';
import { YesNoRadioGroup } from './YesNoRadioGroup';
import type {
  EndorsementFormData,
  EndorsementModalProps,
  EndorsementPayload,
  WorkerSuggestion,
} from './types';

type WorkerOption = WorkerSuggestion & { value: number; label: string };

const formatWorkerOption = (option: unknown) => {
  const worker = option as WorkerOption;
  return (
    <Group gap="sm" wrap="nowrap">
      <Avatar
        src={worker.profileImageUrl || defaultAvatar}
        size={28}
        radius="xl"
      />
      <Text size="sm">{capitalizeName(worker.label)}</Text>
    </Group>
  );
};

// Prevent browser autofill on the AsyncSelect input.
function NoAutofillInput(props: InputProps<WorkerOption, false>) {
  return (
    <selectComponents.Input
      {...props}
      autoComplete="off"
      data-lpignore="true"
      data-1p-ignore="true"
      data-bwignore="true"
      data-form-type="other"
    />
  );
}

function WorkerMenu({
  selectProps,
  children,
  ...menuProps
}: MenuProps<WorkerOption, false>) {
  const { onAddNonTH } = selectProps as typeof selectProps & {
    onAddNonTH?: () => void;
  };
  return (
    <selectComponents.Menu selectProps={selectProps} {...menuProps}>
      {children}
      <Divider />
      <Button
        variant="light"
        fullWidth
        c="var(--black)"
        bdrs={0}
        onClick={(event) => {
          event.preventDefault();
          onAddNonTH?.();
        }}
      >
        + Endorse someone not on Trusted Herd
      </Button>
    </selectComponents.Menu>
  );
}

function EndorsementModal({
  opened,
  onClose,
  onSubmit: submitPayload,
  api,
  eventTypes,
  traits,
}: EndorsementModalProps) {
  const { showWarningNotification } = useNotification();
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedWorker, setSelectedWorker] = useState<WorkerSuggestion | null>(
    null
  );
  const [isNonTHMode, setIsNonTHMode] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const handleInputChange = (value: string, { action }: InputActionMeta) => {
    if (action === 'input-blur' || action === 'menu-close') return inputValue;
    setInputValue(value);
    return value;
  };

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { isSubmitting },
  } = useForm<EndorsementFormData>({
    mode: 'onChange',
    defaultValues: {
      email: '',
      fullName: '',
      companyName: '',
      reviewDescription: '',
      photoIsRepresentative: undefined,
      isEndorsedManager: false,
      sharedEventsNumber: undefined,
      sharedEventsType: [],
      endorsedTraits: [],
    },
  });

  useEffect(() => {
    if (!opened) {
      reset();
      setStep(1);
      setSelectedWorker(null);
      setIsNonTHMode(false);
      setInputValue('');
    }
  }, [opened, reset]);

  const loadWorkerOptions = async (
    searchText: string
  ): Promise<WorkerOption[]> => {
    const query = searchText.trim();
    const looksLikeEmail = query.includes('@');
    if (looksLikeEmail && !isValidEmail(query)) return [];
    if (!looksLikeEmail && query.length < MIN_SEARCH_CHARS) return [];
    try {
      const workers = looksLikeEmail
        ? await api.searchWorkerByEmail(query)
        : await api.searchWorkers(query);
      return workers.map((worker) => ({
        ...worker,
        value: worker.id,
        label: worker.name,
      }));
    } catch {
      return [];
    }
  };

  const handleWorkerSelect = (option: SingleValue<WorkerOption>) => {
    // Reset the search text on select/clear: we neutralized react-select's
    // menu-close clear, so the auto-reset after picking must be done here.
    setInputValue('');

    if (!option) {
      setSelectedWorker(null);
      setValue('workerId', undefined);
      setValue('fullName', '');
      setValue('email', '');
      setValue('photoIsRepresentative', undefined);
      return;
    }

    if (option.alreadyEndorsed) {
      showWarningNotification(
        'You have already endorsed this worker. Please select another worker.',
        { title: 'Already endorsed' }
      );
      return;
    }

    const worker: WorkerSuggestion = {
      id: option.value,
      name: option.label,
      email: option.email,
      profileImageUrl: option.profileImageUrl,
      alreadyEndorsed: option.alreadyEndorsed,
    };

    setSelectedWorker(worker);
    setValue('workerId', worker.id);
    setValue('fullName', worker.name);
    setValue('email', worker.email || '');
  };

  const handleEnterNonTH = () => {
    setIsNonTHMode(true);
    setSelectedWorker(null);
    setValue('workerId', undefined);
    setValue('fullName', '');
    setValue('email', '');
    setValue('photoIsRepresentative', undefined);
  };

  const handleExitNonTH = () => {
    setIsNonTHMode(false);
    setValue('fullName', '');
    setValue('email', '');
  };

  const handleContinueToStep2 = () => {
    if (isNonTHMode) {
      const fullName = (watch('fullName') || '').trim();
      const email = watch('email');
      // Backend derives first/last from a space split, so require both names.
      const hasFirstAndLast = fullName.split(/\s+/).filter(Boolean).length >= 2;
      if (!hasFirstAndLast || !isValidEmail(email)) {
        showWarningNotification(
          "Please enter the worker's first and last name and a valid email.",
          { title: 'Missing information' }
        );
        return;
      }
      setStep(2);
      return;
    }

    const photoIsRep = watch('photoIsRepresentative');
    if (!selectedWorker || photoIsRep === undefined) {
      showWarningNotification(
        'Please select a worker and answer if their photo is representative',
        { title: 'Missing information' }
      );
      return;
    }
    setStep(2);
  };

  const handleBackToStep1 = () => {
    setStep(1);
  };

  const onSubmit = async (data: EndorsementFormData) => {
    const payload: EndorsementPayload = {
      workerId: data.workerId || null,
      email: data.email,
      fullName: data.fullName,
      companyName: data.companyName,
      reviewDescription: data.reviewDescription || null,
      photoIsRepresentative: data.photoIsRepresentative ?? null,
      isEndorsedManager: data.isEndorsedManager,
      sharedEventsNumber: data.sharedEventsNumber,
      sharedEventsType: data.sharedEventsType,
      endorsedTraits: data.endorsedTraits,
    };
    await submitPayload(payload);
  };

  const onInvalidSubmit = (formErrors: Record<string, unknown>) => {
    const errorFields = Object.keys(formErrors);
    if (errorFields.length > 0) {
      showWarningNotification(
        `Please fill in all required fields: ${errorFields.join(', ')}`,
        { title: 'Form incomplete' }
      );
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      centered
      keepMounted={false}
      // Legacy CSS uses z-index up to 1060 (Bootstrap modals). Bump above to sit on top.
      zIndex={1100}
      closeButtonProps={{ 'aria-label': 'Close' }}
      title={
        <Flex align="center" gap="md">
          {step === 2 && !isMobile && (
            <ActionIcon variant="subtle" onClick={handleBackToStep1}>
              <HiArrowLeft size={24} color="black" />
            </ActionIcon>
          )}
          <Title order={3}>Endorse Worker</Title>
        </Flex>
      }
    >
      <form
        onSubmit={(event) => {
          // Opt out of Grails th.js global delegated submit handler that appends
          // <div class="loading-modal"> to <body> and never removes it for non-jQuery
          // form posts. The legacy handler checks event.originalEvent.preventLoader.
          const nativeEvent = event.nativeEvent as Event & {
            preventLoader?: boolean;
          };
          nativeEvent.preventLoader = true;
          return handleSubmit(onSubmit, onInvalidSubmit)(event);
        }}
      >
        {step === 1 && !isNonTHMode && (
          <Stack gap="md">
            <div>
              <Text size="sm" fw={500} mb={4}>
                Worker Name or Email *
              </Text>
              <AsyncSelect
                cacheOptions
                loadOptions={loadWorkerOptions}
                inputValue={inputValue}
                onInputChange={handleInputChange}
                onChange={(newValue) =>
                  handleWorkerSelect(newValue as SingleValue<WorkerOption>)
                }
                placeholder="Search by name or email..."
                value={
                  selectedWorker
                    ? {
                        ...selectedWorker,
                        value: selectedWorker.id,
                        label: selectedWorker.name,
                      }
                    : null
                }
                formatOptionLabel={formatWorkerOption}
                components={{
                  Menu: WorkerMenu,
                  Input: NoAutofillInput,
                  DropdownIndicator: null,
                }}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                maxMenuHeight={260}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 1200 }),
                }}
                {...{ onAddNonTH: handleEnterNonTH }}
                isClearable
              />
            </div>

            {selectedWorker && (
              <Group gap="md" mt="md">
                <Avatar
                  src={selectedWorker.profileImageUrl || defaultAvatar}
                  size={75}
                  radius={4}
                />
                <div>
                  <Text size="lg" fw={600}>
                    {capitalizeName(selectedWorker.name)}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {selectedWorker.email || ''}
                  </Text>
                </div>
              </Group>
            )}

            <div>
              <Text size="sm" fw={500} mb={4}>
                Does their picture fairly portray them? *
              </Text>
              <Controller
                control={control}
                name="photoIsRepresentative"
                render={({ field }) => (
                  <YesNoRadioGroup
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          </Stack>
        )}

        {step === 1 && isNonTHMode && (
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              This worker isn&apos;t on Trusted Herd. Enter their details to
              endorse them.
            </Text>
            <Controller
              control={control}
              name="fullName"
              render={({ field }) => (
                <TextInput
                  label="Full Name *"
                  placeholder="First and last name"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              control={control}
              name="email"
              render={({ field }) => (
                <TextInput
                  type="email"
                  label="Email *"
                  placeholder="worker@example.com"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <Anchor size="sm" onClick={handleExitNonTH}>
              ← Back to search
            </Anchor>
          </Stack>
        )}

        {step === 2 && (selectedWorker || isNonTHMode) && (
          <Step2Content
            selectedWorker={
              selectedWorker ?? {
                id: 0,
                name: watch('fullName'),
                email: watch('email'),
                profileImageUrl: '',
                alreadyEndorsed: false,
              }
            }
            control={control}
            setValue={setValue}
            watch={watch}
            eventTypes={eventTypes}
            traits={traits}
            api={api}
            isSubmitting={isSubmitting}
            onBack={handleBackToStep1}
          />
        )}

        {step === 1 && (
          <Group justify="flex-end" mt="xl">
            <Button onClick={handleContinueToStep2}>Continue</Button>
          </Group>
        )}
      </form>
    </Modal>
  );
}

export default EndorsementModal;
